import { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HiClipboardDocument, HiCheckCircle } from 'react-icons/hi2';
import { useCreateCustomerUploadLinkMutation } from '@/store/customerUploadLinksApi';
import type { CustomerUploadLinkDto } from '@/types';
import {
  SuccessContainer,
  UrlDisplay,
  UrlText,
  SizeLimitGroup,
  SizeLimitValue,
} from './GenerateUploadLinkModal.styled';

interface GenerateUploadLinkModalProps {
  show: boolean;
  onHide: () => void;
  customerId: string;
  customerEmail?: string | null;
  sprawy: Array<{ id: string; signature: string }>;
}

interface FormValues {
  noExpiry: boolean;
  expiresInHours: number;
  sprawaId: string;
  maxSizeMb: number;
  message: string;
  sendEmail: boolean;
}

const DEFAULTS: FormValues = {
  noExpiry: false,
  expiresInHours: 72,
  sprawaId: '',
  maxSizeMb: 500,
  message: '',
  sendEmail: false,
};

const EXPIRY_OPTIONS = [
  { value: 24, labelKey: 'customerUploadLink.expiry.24h' },
  { value: 48, labelKey: 'customerUploadLink.expiry.48h' },
  { value: 72, labelKey: 'customerUploadLink.expiry.72h' },
  { value: 168, labelKey: 'customerUploadLink.expiry.7d' },
  { value: 720, labelKey: 'customerUploadLink.expiry.30d' },
];

export default function GenerateUploadLinkModal({
  show,
  onHide,
  customerId,
  customerEmail,
  sprawy,
}: GenerateUploadLinkModalProps) {
  const { t } = useTranslation();
  const [createLink, { isLoading }] = useCreateCustomerUploadLinkMutation();
  const [generatedLink, setGeneratedLink] = useState<CustomerUploadLinkDto | null>(null);
  const [copied, setCopied] = useState(false);

  const { register, handleSubmit, watch, reset } = useForm<FormValues>({ defaultValues: DEFAULTS });
  const noExpiry = watch('noExpiry');
  const maxSizeMb = watch('maxSizeMb');

  const handleEnter = () => {
    reset(DEFAULTS);
    setGeneratedLink(null);
    setCopied(false);
  };

  const submit = handleSubmit(async (values) => {
    const result = await createLink({
      customerId,
      sprawaId: values.sprawaId || undefined,
      expiresInHours: values.noExpiry ? null : Number(values.expiresInHours),
      maxSizeMb: Number(values.maxSizeMb),
      message: values.message || undefined,
      sendEmail: values.sendEmail,
    }).unwrap();
    setGeneratedLink(result);
  });

  const handleCopy = async () => {
    if (!generatedLink) return;
    await navigator.clipboard.writeText(generatedLink.uploadUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const hasEmail = !!customerEmail;

  return (
    <Modal show={show} onHide={onHide} onEnter={handleEnter} centered>
      {generatedLink ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title>{t('customerUploadLink.generated.title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SuccessContainer>
              <HiCheckCircle size="2.5rem" className="text-success" />
              <p className="text-center mb-0">
                {t('customerUploadLink.generated.description')}
              </p>
              <UrlDisplay>
                <UrlText>{generatedLink.uploadUrl}</UrlText>
                <Button variant="outline-secondary" size="sm" onClick={handleCopy}>
                  {copied ? <HiCheckCircle size="1rem" /> : <HiClipboardDocument size="1rem" />}
                </Button>
              </UrlDisplay>
            </SuccessContainer>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={onHide}>
              {t('common.close')}
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <Form onSubmit={submit}>
          <Modal.Header closeButton>
            <Modal.Title>{t('customerUploadLink.modal.title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label={t('customerUploadLink.modal.noExpiry')}
                {...register('noExpiry')}
              />
            </Form.Group>

            {!noExpiry && (
              <Form.Group className="mb-3">
                <Form.Label>{t('customerUploadLink.modal.expiresIn')}</Form.Label>
                <Form.Select {...register('expiresInHours', { valueAsNumber: true })}>
                  {EXPIRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {sprawy.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>{t('customerUploadLink.modal.sprawa')}</Form.Label>
                <Form.Select {...register('sprawaId')}>
                  <option value="">{t('customerUploadLink.modal.noSprawa')}</option>
                  {sprawy.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.signature}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>{t('customerUploadLink.modal.maxSize')}</Form.Label>
              <SizeLimitGroup>
                <Form.Range
                  min={10}
                  max={2048}
                  step={10}
                  {...register('maxSizeMb', { valueAsNumber: true })}
                />
                <SizeLimitValue>{maxSizeMb} MB</SizeLimitValue>
              </SizeLimitGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('customerUploadLink.modal.message')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder={t('customerUploadLink.modal.messagePlaceholder')}
                maxLength={1000}
                {...register('message')}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                id="send-email-checkbox"
                type="checkbox"
                label={t('customerUploadLink.modal.sendEmail')}
                disabled={!hasEmail}
                {...register('sendEmail')}
              />
              {!hasEmail && (
                <Form.Text muted>{t('customerUploadLink.modal.noEmail')}</Form.Text>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={onHide}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {t('customerUploadLink.modal.generate')}
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
}
